import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRenderedMediaUpload } from "@/hooks/useRenderedMediaUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { 
  Share2, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Send, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Calendar,
  ShoppingBag,
  Heart,
  Sun,
  Gift,
  Mail,
  Settings,
  Plus,
  Eye,
  Trash2,
  Edit,
  Image as ImageIcon,
  FileText,
  Video,
  Upload,
  X,
  Link as LinkIcon
} from 'lucide-react';
import { Link } from "react-router-dom";
import logoIcon from "@/assets/regard-de-nature-logo.png";
import { EntityType, DraftStatus, SocialChannels, CopyContent, ENTITY_TYPE_LABELS, getShareUrl } from "@/types/socialPublishing";
import { BrandedPreview } from "@/components/social/BrandedPreview";
import { VideoReelsDrafts } from "@/components/social/VideoReelsDrafts";
import { EmojiPickerPopover } from "@/components/social/EmojiPickerPopover";
import { GiphyPickerPopover } from "@/components/social/GiphyPickerPopover";
import { ImageEditor, DEFAULT_EDITOR_STATE, renderEditedImage, renderEditedImageWithoutGifs, type ImageEditorState } from "@/components/social/ImageEditor";
import { Wand2 } from "lucide-react";

// Types locaux pour les drafts
// Hashtags prédéfinis naturopathie
const NATUROPATHY_HASHTAGS = [
  '#naturopathie', '#bienetre', '#sante', '#naturel', '#iridologie',
  '#phytotherapie', '#aromatherapie', '#gemmotherapie', '#hygienevitale',
  '#santeholistique', '#medecinenatturelle', '#fleursdebach', '#equilibre',
  '#vitalite', '#terrainbiologique', '#preventionsante', '#naturopathe',
  '#bienetrenaturel', '#conseilssante', '#hygienedevie'
];

interface DraftItem {
  id: string;
  title: string;
  entity_type: EntityType;
  entity_id: string | null;
  status: DraftStatus;
  channels: SocialChannels;
  copy: CopyContent;
  media: any;
  render_mode: 'legacy_image' | 'screenshot_share';
  share_url: string | null;
  schedule: { publish_at: string | null; timezone: string };
  published_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  // Branding fields
  visual_title: string | null;
  visual_title_size: number;
  show_logo: boolean;
  show_site_name: boolean;
  show_site_url: boolean;
}

interface ContentEntity {
  id: string;
  title: string;
  description?: string;
  image_url?: string;
  is_active: boolean;
  slug?: string;
}

interface GlobalSettings {
  make_webhook_social_url: string | null;
  social_publish_enabled: boolean;
  social_publish_mode: 'test' | 'production';
}

function parseEdgeFunctionError(err: any): { code?: string; message: string } {
  // Supabase Functions errors often look like:
  // { message: "Edge Function returned a non-2xx status code", context: { status: 400, body: "{...}" } }
  const generic = typeof err?.message === 'string' ? err.message : 'Erreur inconnue';

  const body = err?.context?.body;
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      const code = parsed?.error || parsed?.code;
      const message = parsed?.message || parsed?.error_description || generic;
      return { code, message };
    } catch {
      return { message: body || generic };
    }
  }

  if (body && typeof body === 'object') {
    const code = (body as any)?.error || (body as any)?.code;
    const message = (body as any)?.message || generic;
    return { code, message };
  }

  return { message: generic };
}

export default function SocialPublishing() {
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EntityType>('accompagnement');
  
  // Modal d'édition/création
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentDraft, setCurrentDraft] = useState<Partial<DraftItem> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Contenus disponibles pour création
  const [availableContent, setAvailableContent] = useState<ContentEntity[]>([]);
  
  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDraft, setPreviewDraft] = useState<DraftItem | null>(null);
  
  // Hook pour le rendu des visuels brandés
  const { renderAndUploadMedia, renderAndUploadCarousel, isRendering } = useRenderedMediaUpload();
  
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Charger les paramètres
      const { data: settingsData } = await supabase
        .from('app_settings')
        .select('make_webhook_social_url, social_publish_enabled, social_publish_mode')
        .limit(1)
        .single();
      
      setSettings(settingsData as GlobalSettings);

      // Charger les brouillons de publication
      const { data: draftsData, error: draftsError } = await supabase
        .from('social_publication_drafts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (draftsError) {
        console.error('Error loading drafts:', draftsError);
      } else {
        const rawDrafts = (draftsData || []) as unknown as DraftItem[];

        // Normalisation défensive : si published_at existe, le statut doit être 'published'
        const toFix = rawDrafts
          .filter(d => !!d.published_at && d.status !== 'published')
          .map(d => d.id);

        if (toFix.length > 0) {
          console.warn('[SocialPublishing] Fixing inconsistent drafts (published_at set but status != published):', toFix);
          await supabase
            .from('social_publication_drafts')
            .update({ status: 'published', last_error: null })
            .in('id', toFix);
        }

        const normalizedDrafts = rawDrafts.map(d =>
          d.published_at
            ? ({ ...d, status: 'published' as DraftStatus, last_error: null } as DraftItem)
            : d
        );

        setDrafts(normalizedDrafts);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Charger le contenu disponible quand on change d'onglet
  useEffect(() => {
    if (activeTab && activeTab !== 'manuel') {
      loadAvailableContent(activeTab);
    } else {
      setAvailableContent([]);
    }
  }, [activeTab]);

  const loadAvailableContent = async (entityType: EntityType) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || entityType === 'manuel') return;

    try {
      let data: any[] | null = null;
      
      if (entityType === 'accompagnement') {
        const res = await supabase.from('accompaniments').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'evenement') {
        const res = await supabase.from('events').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'boutique') {
        const res = await supabase.from('products').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'bien-etre-saisonnier') {
        const res = await supabase.from('seasonal_wellness').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'cartes-cadeaux') {
        const res = await supabase.from('gift_card_settings').select('*').eq('user_id', user.id);
        data = res.data;
      }
      
      if (data) {
        const mapped = data.map((item: any) => ({
          id: item.id,
          title: item.title || item.name || item.season || 'Carte cadeau',
          description: item.short_description || item.description,
          image_url: item.image_url || item.cover_image_url,
          is_active: item.is_active !== false,
          slug: item.slug || item.id,
        }));
        setAvailableContent(mapped);
      }
    } catch (e) {
      console.error('Error loading content:', e);
    }
  };

  // Source de l'image de couverture (upload depuis l'appareil ou via URL)
  const [coverSource, setCoverSource] = useState<'upload' | 'url'>('upload');
  const [uploadingImage, setUploadingImage] = useState(false);

  const uploadImageFile = async (file: File): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const fileName = `social-uploads/${user.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage
        .from('assets')
        .upload(fileName, file, { contentType: file.type || 'image/jpeg', upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('assets').getPublicUrl(fileName);
      return data?.publicUrl || null;
    } catch (e: any) {
      console.error('Upload error:', e);
      toast({ title: 'Erreur upload', description: e.message, variant: 'destructive' });
      return null;
    }
  };

  const handleCoverFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentDraft) return;
    setUploadingImage(true);
    const url = await uploadImageFile(file);
    setUploadingImage(false);
    e.target.value = '';
    if (!url) return;
    setCurrentDraft({
      ...currentDraft,
      media: {
        ...currentDraft.media,
        cover: { ...((currentDraft.media as any)?.cover || {}), url, type: 'image', width: 1080, height: 1080 },
      },
    });
  };

  const handleCarouselFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !currentDraft) return;
    setUploadingImage(true);
    const uploaded: { url: string; type: string; width: number; height: number }[] = [];
    for (const file of files) {
      const url = await uploadImageFile(file);
      if (url) uploaded.push({ url, type: 'image', width: 1080, height: 1080 });
    }
    setUploadingImage(false);
    e.target.value = '';
    if (!uploaded.length) return;
    const existing = ((currentDraft.media as any)?.carousel?.items || []) as any[];
    setCurrentDraft({
      ...currentDraft,
      media: {
        ...currentDraft.media,
        mode: 'image_or_carousel',
        carousel: { items: [...existing, ...uploaded] },
      },
    });
  };

  // === Éditeur d'image (filtres, crop, stickers) ===
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorState, setEditorState] = useState<ImageEditorState>(DEFAULT_EDITOR_STATE);
  const [applyingEdit, setApplyingEdit] = useState(false);
  // null = cover image ; sinon index dans carousel.items
  const [editingCarouselIdx, setEditingCarouselIdx] = useState<number | null>(null);

  const openImageEditor = (carouselIdx: number | null = null) => {
    setEditingCarouselIdx(carouselIdx);
    if (carouselIdx === null) {
      const saved = (currentDraft?.media as any)?.editor as ImageEditorState | undefined;
      setEditorState(saved || DEFAULT_EDITOR_STATE);
    } else {
      const items = ((currentDraft?.media as any)?.carousel?.items || []) as any[];
      const saved = items[carouselIdx]?.editor as ImageEditorState | undefined;
      setEditorState(saved || DEFAULT_EDITOR_STATE);
    }
    setEditorOpen(true);
  };

  const getEditorSourceUrl = (): string | null => {
    if (!currentDraft) return null;
    if (editingCarouselIdx === null) {
      return (currentDraft.media as any)?.cover?.originalUrl
        || (currentDraft.media as any)?.cover?.url
        || null;
    }
    const items = ((currentDraft.media as any)?.carousel?.items || []) as any[];
    const item = items[editingCarouselIdx];
    return item?.originalUrl || item?.url || null;
  };

  const applyImageEdit = async () => {
    if (!currentDraft) return;
    const sourceUrl = getEditorSourceUrl();
    if (!sourceUrl) {
      toast({ title: "Aucune image source", variant: "destructive" });
      return;
    }
    setApplyingEdit(true);
    try {
      const { aspectRatio } = editorState;
      const out = aspectRatio === '9:16'
        ? { width: 1080, height: 1920 }
        : aspectRatio === '4:5'
          ? { width: 1080, height: 1350 }
          : { width: 1080, height: 1080 };
      const hasAnimatedGifs = (editorState.stickers || []).some((s) => s.type === 'gif');

      let editedUrl: string | null = null;
      let animatedVideoUrl: string | null = null;

      if (hasAnimatedGifs) {
        // Render background image WITHOUT gifs, then compose animated MP4 server-side
        const { blob, gifs } = await renderEditedImageWithoutGifs(sourceUrl, editorState, out);
        if (!blob) throw new Error("Échec du rendu (background)");
        const file = new File([blob], `bg-${Date.now()}.jpg`, { type: 'image/jpeg' });
        editedUrl = await uploadImageFile(file);
        if (!editedUrl) throw new Error("Échec de l'upload (background)");

        const { data: composed, error: composeErr } = await supabase.functions.invoke('compose-animated-post', {
          body: {
            background_image_url: editedUrl,
            width: out.width,
            height: out.height,
            duration_sec: 6,
            gifs,
          },
        });
        if (composeErr || !composed?.video_url) {
          console.error('compose-animated-post failed:', composeErr, composed);
          toast({ title: 'GIF animé indisponible', description: 'Publication en image figée.', variant: 'destructive' });
        } else {
          animatedVideoUrl = composed.video_url;
        }
      } else {
        const blob = await renderEditedImage(sourceUrl, editorState, out);
        if (!blob) throw new Error("Échec du rendu");
        const file = new File([blob], `edited-${Date.now()}.jpg`, { type: 'image/jpeg' });
        editedUrl = await uploadImageFile(file);
        if (!editedUrl) throw new Error("Échec de l'upload");
      }

      if (editingCarouselIdx === null) {
        const cover = (currentDraft.media as any)?.cover || {};
        setCurrentDraft({
          ...currentDraft,
          media: {
            ...currentDraft.media,
            cover: {
              ...cover,
              originalUrl: cover.originalUrl || cover.url,
              url: editedUrl,
              videoUrl: animatedVideoUrl || undefined,
              animated: !!animatedVideoUrl,
              width: out.width,
              height: out.height,
              type: animatedVideoUrl ? 'video' : 'image',
            },
            editor: editorState,
          } as any,
        });
      } else {
        const items = [...(((currentDraft.media as any)?.carousel?.items || []) as any[])];
        const item = items[editingCarouselIdx] || {};
        items[editingCarouselIdx] = {
          ...item,
          originalUrl: item.originalUrl || item.url,
          url: editedUrl,
          videoUrl: animatedVideoUrl || undefined,
          animated: !!animatedVideoUrl,
          width: out.width,
          height: out.height,
          type: animatedVideoUrl ? 'video' : 'image',
          editor: editorState,
        };
        setCurrentDraft({
          ...currentDraft,
          media: {
            ...currentDraft.media,
            carousel: { items },
          } as any,
        });
      }
      toast({ title: "Image éditée", description: "Modifications appliquées avec succès." });
      setEditorOpen(false);
      setEditingCarouselIdx(null);
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message || "Impossible d'appliquer", variant: "destructive" });
    } finally {
      setApplyingEdit(false);
    }
  };

  const resetImageEdit = () => {
    if (!currentDraft) return;
    const cover = (currentDraft.media as any)?.cover || {};
    if (cover.originalUrl) {
      setCurrentDraft({
        ...currentDraft,
        media: {
          ...currentDraft.media,
          cover: { ...cover, url: cover.originalUrl, originalUrl: undefined },
          editor: undefined,
        } as any,
      });
      toast({ title: "Image originale restaurée" });
    }
  };

  const removeCarouselItem = (idx: number) => {
    if (!currentDraft) return;
    const existing = ((currentDraft.media as any)?.carousel?.items || []) as any[];
    const next = existing.filter((_, i) => i !== idx);
    setCurrentDraft({
      ...currentDraft,
      media: { ...currentDraft.media, carousel: { items: next } },
    });
  };


  // Filtrer les drafts par type
  const filteredDrafts = useMemo(() => {
    return drafts.filter(d => d.entity_type === activeTab);
  }, [drafts, activeTab]);

  const openCreateModal = (entityId?: string) => {
    const entity = entityId ? availableContent.find(c => c.id === entityId) : null;
    
    const newDraft: Partial<DraftItem> = {
      entity_type: activeTab,
      entity_id: entityId || null,
      title: entity?.title || '',
      status: 'pending',
      channels: {
        instagram_post: false,
        instagram_carousel: false,
        instagram_story: false,
        instagram_reel: false,
        facebook_page: false,
        facebook_carousel: false,
        facebook_story: false,
        facebook_reel: false,
        linkedin_page: false,
        linkedin_reel: false,
        tiktok: false,
        newsletter: false,
      },
      copy: {
        default: '',
        instagram: { caption: null, hashtags: null, alt_text: null },
        facebook: { message: null },
        linkedin: { text: null },
        story: { text: null, cta: { label: null, url: null } },
        reel: { caption: null },
        email: { subject: null, preheader: null },
      },
      media: {
        mode: 'image',
        cover: { url: entity?.image_url || null, type: 'image', width: 1080, height: 1080 },
        carousel: { items: [] },
        video: { url: null, type: null, duration_seconds: null, poster_url: null, width: null, height: null },
        story: { image_url: entity?.image_url || null, video_url: null, type: 'image', width: 1080, height: 1920 },
        reel: { video_url: null, type: null, duration_seconds: null, poster_url: null, width: 1080, height: 1920 },
      },
      render_mode: 'legacy_image',
      share_url: entity?.slug && activeTab !== 'manuel' ? getShareUrl(activeTab, entity.slug) : null,
      schedule: { publish_at: null, timezone: 'Europe/Paris' },
      // Branding defaults
      visual_title: entity?.title || null,
      show_logo: true,
      show_site_name: true,
      show_site_url: true,
    };
    
    setCurrentDraft(newDraft);
    setIsCreating(true);
    setEditModalOpen(true);
  };

  const openEditModal = (draft: DraftItem) => {
    setCurrentDraft(draft);
    setIsCreating(false);
    setEditModalOpen(true);
  };

  const saveDraft = async () => {
    if (!currentDraft) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      if (isCreating) {
        // Créer un nouveau brouillon
        const { data, error } = await supabase
          .from('social_publication_drafts')
          .insert({
            user_id: user.id,
            entity_type: currentDraft.entity_type!,
            entity_id: currentDraft.entity_id,
            title: currentDraft.title!,
            status: 'pending',
            channels: currentDraft.channels as any,
            copy: currentDraft.copy as any,
            media: currentDraft.media as any,
            render_mode: currentDraft.render_mode!,
            share_url: currentDraft.share_url,
            schedule: currentDraft.schedule as any,
            visual_title: currentDraft.visual_title,
            show_logo: currentDraft.show_logo ?? true,
            show_site_name: currentDraft.show_site_name ?? true,
            show_site_url: currentDraft.show_site_url ?? true,
          })
          .select()
          .single();

        if (error) throw error;
        setDrafts([data as unknown as DraftItem, ...drafts]);
        toast({ title: "Brouillon créé", description: "Votre brouillon a été enregistré" });
      } else {
        // Mettre à jour un brouillon existant
        const { error } = await supabase
          .from('social_publication_drafts')
          .update({
            title: currentDraft.title,
            channels: currentDraft.channels as any,
            copy: currentDraft.copy as any,
            media: currentDraft.media as any,
            render_mode: currentDraft.render_mode,
            share_url: currentDraft.share_url,
            schedule: currentDraft.schedule as any,
            status: 'ready',
            visual_title: currentDraft.visual_title,
            show_logo: currentDraft.show_logo ?? true,
            show_site_name: currentDraft.show_site_name ?? true,
            show_site_url: currentDraft.show_site_url ?? true,
          })
          .eq('id', currentDraft.id!);

        if (error) throw error;
        setDrafts(drafts.map(d => d.id === currentDraft.id ? { ...d, ...currentDraft, status: 'ready' } as DraftItem : d));
        toast({ title: "Brouillon mis à jour" });
      }

      setEditModalOpen(false);
      setCurrentDraft(null);
    } catch (error: any) {
      console.error('Save error:', error);
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    }
  };

  const deleteDraft = async (id: string) => {
    if (!confirm('Supprimer ce brouillon ?')) return;

    const { error } = await supabase
      .from('social_publication_drafts')
      .delete()
      .eq('id', id);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      setDrafts(drafts.filter(d => d.id !== id));
      toast({ title: "Brouillon supprimé" });
    }
  };

  const handlePreview = (draft: DraftItem) => {
    setPreviewDraft(draft);
    setPreviewOpen(true);
  };

  const handlePublish = async (draft: DraftItem, options?: { force?: boolean }) => {
    // ===== VALIDATION 0: Déjà publié (published_at fait foi) =====
    if (draft.published_at && !options?.force) {
      const publishedAtLabel = new Date(draft.published_at).toLocaleString('fr-FR');

      // Réparer immédiatement l'incohérence éventuelle (status=error mais published_at non-null)
      if (draft.status !== 'published') {
        await supabase
          .from('social_publication_drafts')
          .update({ status: 'published', last_error: null })
          .eq('id', draft.id);

        setDrafts(drafts.map(d =>
          d.id === draft.id ? ({ ...d, status: 'published', last_error: null } as DraftItem) : d
        ));
      }

      toast({
        title: "Déjà publié",
        description: `Ce brouillon a déjà été publié le ${publishedAtLabel}. Utilisez « Republier » pour renvoyer.`,
      });
      return;
    }

    // ===== VALIDATION 1: Mode Production obligatoire =====
    if (settings?.social_publish_mode !== 'production') {
      toast({
        title: "Mode Production requis",
        description: "La publication réseaux sociaux n'est autorisée qu'en mode Production. Changez le mode dans Paramètres.",
        variant: "destructive",
      });
      console.log('[Publish] BLOCKED: Mode is not production, current mode:', settings?.social_publish_mode);
      return;
    }

    // ===== VALIDATION 2: Webhook configuré =====
    if (!settings?.make_webhook_social_url) {
      toast({
        title: "Configuration manquante",
        description: "Configurez l'URL du webhook Make.com dans Paramètres",
        variant: "destructive",
      });
      return;
    }

    // ===== VALIDATION 3: Au moins un canal sélectionné =====
    const hasChannel = Object.values(draft.channels).some(v => v === true);
    if (!hasChannel) {
      toast({
        title: "Aucun canal sélectionné",
        description: "Cochez au moins un canal de publication",
        variant: "destructive",
      });
      return;
    }

    setPublishingId(draft.id);
    
    console.log('========================================');
    console.log('[Publish] START - Draft:', draft.id);
    console.log('[Publish] Mode: PRODUCTION (validated)');
    console.log('========================================');
    
    try {
      // ===== ÉTAPE 1: Génération des visuels brandés =====
      // OBLIGATOIRE: Toujours générer et uploader les images
      toast({
        title: "Génération des visuels...",
        description: "Création des images feed (1080x1080) et story (1080x1920)",
      });

      console.log('[Publish] STEP 1: render:start');
      console.log('[Publish] Render options:', {
        coverImageUrl: draft.media?.cover?.url || null,
        visualTitle: draft.visual_title,
        showLogo: draft.show_logo,
        showSiteName: draft.show_site_name,
        draftId: draft.id,
      });

      const renderedMedia = await renderAndUploadMedia({
        coverImageUrl: draft.media?.cover?.url || null,
        visualTitle: draft.visual_title,
        visualTitleSize: (draft as any).visual_title_size || 48,
        visualTitlePosition: (draft.media as any)?.visual_title_position || 'middle-center',
        showLogo: draft.show_logo,
        showSiteName: draft.show_site_name,
        showSiteUrl: draft.show_site_url ?? true,
        draftId: draft.id,
      });

      console.log('[Publish] STEP 1: render:done');
      console.log('[Publish] feed_url:', renderedMedia.feed_url || 'NULL');
      console.log('[Publish] story_url:', renderedMedia.story_url || 'NULL');

      // Vérifier que les URLs ont été générées
      if (!renderedMedia.feed_url || !renderedMedia.story_url) {
        console.error('[Publish] CRITICAL: Rendered URLs are null!');
        throw new Error('Échec de la génération des visuels. Vérifiez la console pour plus de détails.');
      }

      console.log('[Publish] STEP 1: upload:done - Both URLs generated successfully');

      // ===== ÉTAPE 1bis: Branding des images du carrousel (optionnel) =====
      const brandCarousel = (draft.media as any)?.brand_carousel === true;
      const existingCarouselItems = ((draft.media as any)?.carousel?.items || []) as any[];
      let updatedCarouselItems = existingCarouselItems;

      if (brandCarousel && existingCarouselItems.length > 0) {
        console.log('[Publish] STEP 1bis: Branding', existingCarouselItems.length, 'carousel slides');
        toast({
          title: "Branding du carrousel...",
          description: `Application du branding sur ${existingCarouselItems.length} image(s) du carrousel`,
        });
        const slideItems = existingCarouselItems
          .map((it: any) => {
            const url = it.image_url || it.url;
            const b = it.branding || {};
            return url
              ? {
                  url,
                  overrides: {
                    visualTitle: b.visual_title !== undefined ? b.visual_title : draft.visual_title,
                    showLogo: b.show_logo !== undefined ? b.show_logo : draft.show_logo,
                    showSiteName: b.show_site_name !== undefined ? b.show_site_name : draft.show_site_name,
                    showSiteUrl: b.show_site_url !== undefined ? b.show_site_url : (draft.show_site_url ?? true),
                  },
                }
              : null;
          })
          .filter(Boolean) as Array<{ url: string; overrides: any }>;
        const brandedUrls = await renderAndUploadCarousel(slideItems, {
          visualTitle: draft.visual_title,
          visualTitleSize: (draft as any).visual_title_size || 48,
          visualTitlePosition: (draft.media as any)?.visual_title_position || 'middle-center',
          showLogo: draft.show_logo,
          showSiteName: draft.show_site_name,
          showSiteUrl: draft.show_site_url ?? true,
          draftId: draft.id,
        });
        updatedCarouselItems = existingCarouselItems.map((it: any, i: number) => ({
          ...it,
          url: brandedUrls[i] || it.url,
          image_url: brandedUrls[i] || it.image_url || it.url,
          original_url: it.original_url || it.image_url || it.url,
        }));
        console.log('[Publish] STEP 1bis: carousel branding done');
      }

      // Mettre à jour le draft avec les URLs rendues (pour historique)
      const updatedMedia = {
        ...draft.media,
        carousel: {
          ...((draft.media as any)?.carousel || {}),
          items: updatedCarouselItems,
        },
        rendered: {
          feed_url: renderedMedia.feed_url,
          story_url: renderedMedia.story_url,
        },
      };

      await supabase
        .from('social_publication_drafts')
        .update({ media: updatedMedia as any })
        .eq('id', draft.id);

      console.log('[Publish] Rendered URLs saved to DB');

      // ===== ÉTAPE 2: Envoi vers Make.com =====
      console.log('[Publish] STEP 2: make:post - Calling Edge Function');
      console.log('[Publish] rendered_media payload:', {
        feed_url: renderedMedia.feed_url,
        story_url: renderedMedia.story_url,
      });

      // Si la cover éditée est une animation (GIFs composés en MP4), on transmet aussi video_url
      const animatedVideoUrl = (draft.media as any)?.cover?.videoUrl || ((draft.media as any)?.cover?.animated ? (draft.media as any)?.cover?.url : null);
      if (animatedVideoUrl) {
        console.log('[Publish] Animated cover detected, video_url:', animatedVideoUrl);
      }

      const { data, error } = await supabase.functions.invoke('social-push-to-make', {
        body: { 
          action: 'publish_draft',
          draft_id: draft.id,
          force: options?.force === true,
          rendered_media: {
            feed_url: renderedMedia.feed_url,
            story_url: renderedMedia.story_url,
            video_url: animatedVideoUrl || undefined,
          },
        },
      });
      
      console.log('[Publish] STEP 2: make:post - Response received');
      console.log('[Publish] Response data:', JSON.stringify(data));
      
      if (error) {
        console.error('[Publish] Edge Function error:', error);
        throw error;
      }

      // Mettre à jour le statut local
      setDrafts(drafts.map(d => 
        d.id === draft.id 
          ? { ...d, status: 'published' as DraftStatus, published_at: new Date().toISOString(), media: updatedMedia } 
          : d
      ));

      console.log('========================================');
      console.log('[Publish] SUCCESS - Publication complete');
      console.log('========================================');

      toast({
        title: "Publication réussie !",
        description: "Les visuels ont été générés et envoyés vers Make.com",
      });
      
    } catch (error: any) {
      const parsed = parseEdgeFunctionError(error);

      console.error('========================================');
      console.error('[Publish] ERROR:', parsed.message);
      console.error('Raw error:', error);
      console.error('========================================');

      // Cas fréquent: le brouillon est en réalité déjà publié (published_at set côté DB)
      // → on évite de polluer le statut en "error".
      if (parsed.code === 'ALREADY_PUBLISHED') {
        toast({
          title: "Déjà publié",
          description: parsed.message,
        });
        return;
      }

      // Marquer en erreur
      await supabase
        .from('social_publication_drafts')
        .update({ status: 'error', last_error: parsed.message })
        .eq('id', draft.id);

      setDrafts(drafts.map(d =>
        d.id === draft.id
          ? ({ ...d, status: 'error' as DraftStatus, last_error: parsed.message } as DraftItem)
          : d
      ));

      toast({
        title: "Erreur de publication",
        description: parsed.message,
        variant: "destructive",
      });
    } finally {
      setPublishingId(null);
    }
  };

  const getStatusBadge = (status: DraftStatus) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Publié</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Erreur</Badge>;
      case 'ready':
        return <Badge className="bg-blue-100 text-blue-700"><CheckCircle2 className="w-3 h-3 mr-1" />Prêt</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  const getEntityIcon = (type: EntityType) => {
    switch (type) {
      case 'accompagnement': return <Heart className="w-4 h-4" />;
      case 'evenement': return <Calendar className="w-4 h-4" />;
      case 'boutique': return <ShoppingBag className="w-4 h-4" />;
      case 'bien-etre-saisonnier': return <Sun className="w-4 h-4" />;
      case 'cartes-cadeaux': return <Gift className="w-4 h-4" />;
      case 'manuel': return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  const webhookConfigured = !!settings?.make_webhook_social_url;

  return (
    <PageContainer>
      <PageHeader
        title="Publication Réseaux Sociaux"
        subtitle="Préparez, prévisualisez et publiez vos contenus sur les réseaux sociaux"
        icon={<Share2 className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="secondary"
        actions={
          <div className="flex items-center gap-2">
            {settings?.social_publish_enabled && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                Mode: {settings.social_publish_mode?.toUpperCase()}
              </Badge>
            )}
            <Link to="/account-settings">
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Paramètres
              </Button>
            </Link>
          </div>
        }
      />

      <div className="space-y-6">
        {/* Alerte si non configuré */}
        {!webhookConfigured && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">Configuration requise</p>
                  <p className="text-sm text-amber-700">
                    Configurez l'URL du webhook Make.com dans les paramètres pour activer la publication.
                  </p>
                </div>
                <Link to="/account-settings">
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700">
                    Configurer
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs de haut niveau: Posts vs Vidéos */}
        <Tabs defaultValue="posts" className="space-y-6">
          <TabsList className="h-auto w-full grid grid-cols-2">
            <TabsTrigger value="posts" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <ImageIcon className="w-3 h-3 sm:w-4 sm:h-4" /> 
              <span className="hidden xs:inline">Posts /</span> Images
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Video className="w-3 h-3 sm:w-4 sm:h-4" /> 
              <span className="hidden xs:inline">Vidéos</span> Reels
            </TabsTrigger>
          </TabsList>

          <TabsContent value="posts">
            {/* Tabs par type de contenu - mobile horizontal scroll */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EntityType)}>
              <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
                <TabsList className="inline-flex w-max sm:flex sm:flex-wrap h-auto gap-1 p-1">
                  <TabsTrigger value="accompagnement" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Heart className="w-3 h-3 sm:w-4 sm:h-4" /> 
                    <span className="hidden xs:inline">Accompagne</span>
                    <span className="xs:hidden">Acc.</span>
                  </TabsTrigger>
                  <TabsTrigger value="evenement" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" /> Events
                  </TabsTrigger>
                  <TabsTrigger value="boutique" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4" /> Boutique
                  </TabsTrigger>
                  <TabsTrigger value="bien-etre-saisonnier" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Sun className="w-3 h-3 sm:w-4 sm:h-4" /> Saisonnier
                  </TabsTrigger>
                  <TabsTrigger value="cartes-cadeaux" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Gift className="w-3 h-3 sm:w-4 sm:h-4" /> 
                    <span className="hidden xs:inline">Cartes</span> Cadeaux
                  </TabsTrigger>
                  <TabsTrigger value="manuel" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4" /> Manuel
                  </TabsTrigger>
                </TabsList>
              </div>

          <div className="mt-4 sm:mt-6">
            {/* Bouton créer - mobile responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                {getEntityIcon(activeTab)}
                <span className="truncate">{ENTITY_TYPE_LABELS[activeTab]}</span>
                <Badge variant="secondary" className="flex-shrink-0">{filteredDrafts.length}</Badge>
              </h2>
              
              {activeTab === 'manuel' ? (
                <Button onClick={() => openCreateModal()} size="sm" className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau post
                </Button>
              ) : availableContent.length > 0 ? (
                <Select onValueChange={(v) => openCreateModal(v)}>
                  <SelectTrigger className="w-full sm:w-[280px]">
                    <SelectValue placeholder="Créer depuis..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableContent.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs sm:text-sm text-muted-foreground">Aucun contenu disponible</p>
              )}
            </div>

            {/* Liste des brouillons */}
            {filteredDrafts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Share2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucun brouillon pour cette catégorie</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {activeTab === 'manuel' 
                      ? "Créez un post manuel indépendant"
                      : "Sélectionnez un contenu actif pour créer un brouillon"
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredDrafts.map((draft) => (
                  <Card key={draft.id} className={draft.status === 'error' ? 'border-red-200' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base line-clamp-1">{draft.title}</CardTitle>
                        {getStatusBadge(draft.status)}
                      </div>
                      {draft.last_error && (
                        <p className="text-xs text-red-600 mt-1">{draft.last_error}</p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Canaux sélectionnés */}
                      <div className="flex flex-wrap gap-1">
                        {draft.channels.instagram_post && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Post</Badge>}
                        {(draft.channels as any).instagram_carousel && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Carousel</Badge>}
                        {draft.channels.instagram_story && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Story</Badge>}
                        {draft.channels.instagram_reel && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Reel</Badge>}
                        {draft.channels.facebook_page && <Badge variant="secondary" className="text-xs"><Facebook className="w-3 h-3 mr-1" />Post</Badge>}
                        {(draft.channels as any).facebook_carousel && <Badge variant="secondary" className="text-xs"><Facebook className="w-3 h-3 mr-1" />Carousel</Badge>}
                        {draft.channels.facebook_story && <Badge variant="secondary" className="text-xs"><Facebook className="w-3 h-3 mr-1" />Story</Badge>}
                        {draft.channels.linkedin_page && <Badge variant="secondary" className="text-xs"><Linkedin className="w-3 h-3 mr-1" />Post</Badge>}
                        {draft.channels.newsletter && <Badge variant="secondary" className="text-xs"><Mail className="w-3 h-3 mr-1" />Email</Badge>}
                      </div>

                      {/* Branding indicators */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {draft.show_logo && <span>✓ Logo</span>}
                        {draft.show_site_name && <span>✓ Nom</span>}
                        {draft.visual_title && <span>✓ Titre visuel</span>}
                      </div>

                      {/* Texte par défaut */}
                      {draft.copy.default && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{draft.copy.default}</p>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(draft)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(draft)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteDraft(draft.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            if ((draft.status === 'published' || draft.published_at) && publishingId !== draft.id) {
                              if (window.confirm("Republier ce post sur les réseaux sociaux sélectionnés ?")) {
                                handlePublish(draft, { force: true });
                              }
                              return;
                            }
                            handlePublish(draft);
                          }}
                          disabled={publishingId === draft.id}
                        >
                          {publishingId === draft.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (draft.status === 'published' || draft.published_at) ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Republier
                            </>
                          ) : draft.status === 'error' ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Relancer
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Publier
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Tabs>
          </TabsContent>

          <TabsContent value="videos">
            <VideoReelsDrafts settings={settings} />
          </TabsContent>
        </Tabs>

        {/* Modal d'édition */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isCreating ? 'Nouveau brouillon de publication' : 'Modifier le brouillon'}
              </DialogTitle>
              <DialogDescription>
                Configurez les canaux, textes, médias et branding pour cette publication
              </DialogDescription>
            </DialogHeader>

            {currentDraft && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Colonne gauche : Formulaire */}
                <div className="space-y-6">
                  {/* Titre interne */}
                  <div className="space-y-2">
                    <Label>Titre interne</Label>
                    <Input
                      value={currentDraft.title || ''}
                      onChange={(e) => setCurrentDraft({ ...currentDraft, title: e.target.value })}
                      placeholder="Titre pour identifier cette publication"
                    />
                  </div>

                  <Separator />

                  {/* 1) Branding Section */}
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      1. Branding du visuel
                    </h4>
                    
                    {/* Titre visuel */}
                    <div className="space-y-2">
                      <Label>Titre visuel (optionnel, 1 ligne max)</Label>
                      <Input
                        value={currentDraft.visual_title || ''}
                        onChange={(e) => setCurrentDraft({ ...currentDraft, visual_title: e.target.value })}
                        placeholder="Titre affiché sur l'image"
                        maxLength={60}
                      />
                    </div>

                    {/* Taille du titre visuel */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Taille du titre</Label>
                        <span className="text-xs text-muted-foreground">{currentDraft.visual_title_size || 48}px</span>
                      </div>
                      <input
                        type="range"
                        min="24"
                        max="72"
                        step="2"
                        value={currentDraft.visual_title_size || 48}
                        onChange={(e) => setCurrentDraft({ ...currentDraft, visual_title_size: parseInt(e.target.value) })}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Petit</span>
                        <span>Grand</span>
                      </div>
                    </div>

                    {/* Toggles branding */}
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show-logo" className="cursor-pointer">Afficher le logo</Label>
                      <Switch
                        id="show-logo"
                        checked={currentDraft.show_logo ?? true}
                        onCheckedChange={(checked) => setCurrentDraft({ ...currentDraft, show_logo: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show-site-name" className="cursor-pointer">Afficher le nom du site</Label>
                      <Switch
                        id="show-site-name"
                        checked={currentDraft.show_site_name ?? true}
                        onCheckedChange={(checked) => setCurrentDraft({ ...currentDraft, show_site_name: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="show-site-url" className="cursor-pointer">Afficher l'adresse du site (www.regard-de-nature.fr)</Label>
                      <Switch
                        id="show-site-url"
                        checked={currentDraft.show_site_url ?? true}
                        onCheckedChange={(checked) => setCurrentDraft({ ...currentDraft, show_site_url: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border/40">
                      <Label htmlFor="brand-carousel" className="cursor-pointer">
                        Appliquer le branding à toutes les diapos du carrousel
                        <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                          Titre, logo, nom du site et URL ajoutés sur chaque image
                        </span>
                      </Label>
                      <Switch
                        id="brand-carousel"
                        checked={(currentDraft.media as any)?.brand_carousel ?? false}
                        onCheckedChange={(checked) =>
                          setCurrentDraft({
                            ...currentDraft,
                            media: { ...(currentDraft.media as any), brand_carousel: checked },
                          })
                        }
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* 2) Position du titre visuel sur l'image */}
                  <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
                    <Label className="text-sm font-semibold">2. Position du titre sur l'image</Label>
                    <div className="grid grid-cols-3 gap-1.5 max-w-[220px]">
                      {([
                        'top-left','top-center','top-right',
                        'middle-left','middle-center','middle-right',
                        'bottom-left','bottom-center','bottom-right',
                      ] as const).map((pos) => {
                        const current = (currentDraft.media as any)?.visual_title_position || 'middle-center';
                        const active = current === pos;
                        return (
                          <button
                            key={pos}
                            type="button"
                            onClick={() => setCurrentDraft({
                              ...currentDraft,
                              media: { ...currentDraft.media, visual_title_position: pos },
                            })}
                            className={`aspect-square rounded border flex items-center justify-center transition ${active ? 'border-primary bg-primary/15' : 'border-border hover:border-primary/50'}`}
                            aria-label={pos}
                            title={pos}
                          >
                            <span className={`w-2 h-2 rounded-full ${active ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Separator />

                  {/* 3) Image de couverture */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold">3. Image de couverture</Label>
                      <Select value={coverSource} onValueChange={(v) => setCoverSource(v as 'upload' | 'url')}>
                        <SelectTrigger className="w-[180px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="upload">
                            <div className="flex items-center gap-2"><Upload className="w-4 h-4" />Depuis l'appareil</div>
                          </SelectItem>
                          <SelectItem value="url">
                            <div className="flex items-center gap-2"><LinkIcon className="w-4 h-4" />Depuis une URL</div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {coverSource === 'upload' ? (
                      <div className="space-y-2">
                        <input
                          id="cover-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleCoverFileChange}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={uploadingImage}
                          onClick={() => document.getElementById('cover-upload')?.click()}
                        >
                          {uploadingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Choisir une image
                        </Button>
                        {(currentDraft.media as any)?.cover?.url && (
                          <div className="relative inline-block">
                            <img
                              src={(currentDraft.media as any).cover.url}
                              alt="Couverture"
                              className="w-32 h-32 object-cover rounded border"
                            />
                            <button
                              type="button"
                              onClick={() => setCurrentDraft({
                                ...currentDraft,
                                media: { ...currentDraft.media, cover: { ...((currentDraft.media as any)?.cover || {}), url: null } },
                              })}
                              className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow"
                              aria-label="Retirer l'image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Input
                        value={(currentDraft.media as any)?.cover?.url || ''}
                        onChange={(e) => setCurrentDraft({
                          ...currentDraft,
                          media: {
                            ...currentDraft.media,
                            cover: {
                              ...((currentDraft.media as any)?.cover || {}),
                              url: e.target.value,
                              type: 'image',
                              width: 1080,
                              height: 1080,
                            },
                          },
                        })}
                        placeholder="https://..."
                      />
                    )}
                  </div>

                  {/* 3b) Carrousel d'images (style Instagram) */}
                  <div className="space-y-3 p-4 bg-muted/40 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">Carrousel (images supplémentaires)</Label>
                      <input
                        id="carousel-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleCarouselFilesChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingImage}
                        onClick={() => document.getElementById('carousel-upload')?.click()}
                      >
                        {uploadingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Ajouter des images
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      L'image de couverture est la 1ère diapositive. Ajoutez d'autres images pour créer un carrousel.
                    </p>
                    {((currentDraft.media as any)?.carousel?.items || []).length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[11px] text-muted-foreground">
                          Personnalisez le branding de chaque diapo (titre, logo, nom, URL). Laissez le titre vide pour reprendre le titre principal.
                        </p>
                        {((currentDraft.media as any).carousel.items as any[]).map((item, idx) => {
                          const branding = item.branding || {};
                          const updateItemBranding = (patch: any) => {
                            const items = [...((currentDraft.media as any).carousel.items as any[])];
                            items[idx] = { ...items[idx], branding: { ...(items[idx].branding || {}), ...patch } };
                            setCurrentDraft({
                              ...currentDraft,
                              media: { ...(currentDraft.media as any), carousel: { ...(currentDraft.media as any).carousel, items } },
                            });
                          };
                          const titleVal = branding.visual_title ?? '';
                          const logoVal = branding.show_logo ?? (currentDraft.show_logo ?? true);
                          const siteNameVal = branding.show_site_name ?? (currentDraft.show_site_name ?? true);
                          const siteUrlVal = branding.show_site_url ?? (currentDraft.show_site_url ?? true);
                          return (
                            <div key={idx} className="flex gap-3 p-2 bg-background rounded-lg border">
                              <div className="relative shrink-0">
                                <img src={item.url} alt={`Slide ${idx + 2}`} className="w-24 h-24 object-cover rounded border" />
                                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] bg-black/60 text-white">{idx + 2}</span>
                                <button
                                  type="button"
                                  onClick={() => openImageEditor(idx)}
                                  className="absolute bottom-1 right-1 bg-primary text-primary-foreground rounded-full p-1 shadow"
                                  aria-label="Éditer"
                                  title="Éditer (filtres, emojis, GIFs)"
                                >
                                  <Wand2 className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeCarouselItem(idx)}
                                  className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-1 shadow"
                                  aria-label="Retirer"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="flex-1 space-y-2 min-w-0">
                                <Input
                                  value={titleVal}
                                  onChange={(e) => updateItemBranding({ visual_title: e.target.value })}
                                  placeholder={`Titre de la diapo ${idx + 2}`}
                                  maxLength={60}
                                  className="h-8 text-xs"
                                />
                                <div className="grid grid-cols-3 gap-1.5 text-[11px]">
                                  <label className="flex items-center justify-between gap-1 px-2 py-1 rounded bg-muted/40 cursor-pointer">
                                    <span>Logo</span>
                                    <Switch checked={logoVal} onCheckedChange={(c) => updateItemBranding({ show_logo: c })} />
                                  </label>
                                  <label className="flex items-center justify-between gap-1 px-2 py-1 rounded bg-muted/40 cursor-pointer">
                                    <span>Nom</span>
                                    <Switch checked={siteNameVal} onCheckedChange={(c) => updateItemBranding({ show_site_name: c })} />
                                  </label>
                                  <label className="flex items-center justify-between gap-1 px-2 py-1 rounded bg-muted/40 cursor-pointer">
                                    <span>URL</span>
                                    <Switch checked={siteUrlVal} onCheckedChange={(c) => updateItemBranding({ show_site_url: c })} />
                                  </label>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* 4) Éditeur d'image (filtres, recadrage, stickers, emojis, GIFs) */}
                  {(currentDraft.media as any)?.cover?.url && (
                    <div className="space-y-2 p-3 bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg border border-primary/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-semibold flex items-center gap-1.5">
                            <Wand2 className="w-4 h-4 text-primary" />
                            4. Éditeur d'image (Emoji / GIF)
                          </Label>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            Filtres, recadrage, emojis, GIFs et textes draggables
                          </p>
                        </div>
                        <div className="flex gap-1">
                          {(currentDraft.media as any)?.cover?.originalUrl && (
                            <Button type="button" variant="ghost" size="sm" onClick={resetImageEdit}>
                              Réinitialiser
                            </Button>
                          )}
                          <Button type="button" variant="default" size="sm" onClick={() => openImageEditor(null)}>
                            <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                            Éditer
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* 5) Texte par défaut */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold">5. Texte par défaut</Label>
                      <div className="flex items-center gap-1">
                        <EmojiPickerPopover
                          variant="icon"
                          onSelect={(emoji) => setCurrentDraft({
                            ...currentDraft,
                            copy: { ...currentDraft.copy as CopyContent, default: ((currentDraft.copy as CopyContent)?.default || '') + emoji }
                          })}
                        />
                      </div>
                    </div>
                    <Textarea
                      value={(currentDraft.copy as CopyContent)?.default || ''}
                      onChange={(e) => setCurrentDraft({
                        ...currentDraft,
                        copy: { ...currentDraft.copy as CopyContent, default: e.target.value }
                      })}
                      rows={4}
                      placeholder="Texte de la publication... 🌿"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      💡 Tapez <code className="bg-muted px-1 rounded">@</code> pour mentionner et <code className="bg-muted px-1 rounded">#</code> pour les hashtags. GIFs &amp; stickers : utilisez l'éditeur d'image.
                    </p>
                  </div>

                  <Separator />

                  {/* 6) Hashtags Instagram */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">6. Hashtags Instagram</Label>
                    <Input
                      value={(currentDraft.copy as CopyContent)?.instagram?.hashtags || ''}
                      onChange={(e) => setCurrentDraft({
                        ...currentDraft,
                        copy: { 
                          ...currentDraft.copy as CopyContent, 
                          instagram: { 
                            ...((currentDraft.copy as CopyContent)?.instagram || { caption: null, alt_text: null }),
                            hashtags: e.target.value 
                          }
                        }
                      })}
                      placeholder="#naturopathie #bienetre #sante..."
                    />
                    {/* Hashtags cliquables */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {NATUROPATHY_HASHTAGS.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className="px-2 py-0.5 text-xs bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary rounded-full border transition-colors"
                          onClick={() => {
                            const current = (currentDraft.copy as CopyContent)?.instagram?.hashtags || '';
                            if (!current.includes(tag)) {
                              setCurrentDraft({
                                ...currentDraft,
                                copy: { 
                                  ...currentDraft.copy as CopyContent, 
                                  instagram: { 
                                    ...((currentDraft.copy as CopyContent)?.instagram || { caption: null, alt_text: null }),
                                    hashtags: current ? `${current} ${tag}` : tag
                                  }
                                }
                              });
                            }
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* 7) Canaux de publication */}
                  {(() => {
                    const hasVideoMedia = !!(
                      (currentDraft.media as any)?.video?.url ||
                      (currentDraft.media as any)?.reel?.video_url ||
                      (currentDraft.media as any)?.cover?.videoUrl ||
                      ((currentDraft.media as any)?.cover?.animated && (currentDraft.media as any)?.cover?.url)
                    );
                    const reelsRequested =
                      (currentDraft.channels as any)?.instagram_reel ||
                      (currentDraft.channels as any)?.facebook_reel;
                    return (
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">7. Canaux de publication</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {(['instagram_post', 'instagram_carousel', 'instagram_story', 'instagram_reel', 'facebook_page', 'facebook_carousel', 'facebook_story', 'facebook_reel', 'linkedin_page'] as const).map((channel) => {
                            const isReel = channel.endsWith('_reel');
                            const checked = (currentDraft.channels as any)?.[channel] || false;
                            const willBeBlocked = isReel && checked && !hasVideoMedia;
                            return (
                              <label
                                key={channel}
                                className={`flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-muted ${willBeBlocked ? 'border border-destructive/50 bg-destructive/5' : ''}`}
                                title={willBeBlocked ? 'Un Reel exige une vidéo. Ce canal sera ignoré.' : undefined}
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(c) =>
                                    setCurrentDraft({
                                      ...currentDraft,
                                      channels: { ...currentDraft.channels as SocialChannels, [channel]: !!c }
                                    })
                                  }
                                />
                                {channel.includes('instagram') && <Instagram className="w-4 h-4 text-pink-600" />}
                                {channel.includes('facebook') && <Facebook className="w-4 h-4 text-blue-600" />}
                                {channel.includes('linkedin') && <Linkedin className="w-4 h-4 text-blue-700" />}
                                <span className="text-sm capitalize">{channel.replace('_', ' ')}</span>
                              </label>
                            );
                          })}
                        </div>
                        {reelsRequested && !hasVideoMedia && (
                          <p className="text-xs text-destructive">
                            ⚠️ Un Reel exige une vidéo (MP4). Ajoutez une vidéo ou décochez le canal Reel — sinon il sera ignoré côté publication.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Mode rendu (pour contenus liés uniquement) */}
                  {currentDraft.entity_type !== 'manuel' && (
                    <div className="space-y-2">
                      <Label>Mode de rendu visuel</Label>
                      <Select
                        value={currentDraft.render_mode}
                        onValueChange={(v) => setCurrentDraft({ ...currentDraft, render_mode: v as 'legacy_image' | 'screenshot_share' })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="legacy_image">
                            <div className="flex items-center gap-2">
                              <ImageIcon className="w-4 h-4" />
                              Image directe (legacy)
                            </div>
                          </SelectItem>
                          <SelectItem value="screenshot_share">
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              Capture page /share
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Colonne droite : Aperçu en temps réel (sticky : suit le scroll) */}
                <div className="space-y-4 md:sticky md:top-0 md:self-start md:max-h-[calc(90vh-8rem)] md:overflow-y-auto pr-1">
                  <h4 className="font-semibold text-sm">Aperçu en temps réel</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Feed Preview */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Feed (1:1)</p>
                      <BrandedPreview
                        format="feed"
                        coverImageUrl={(currentDraft.media as any)?.cover?.url || null}
                        visualTitle={currentDraft.visual_title || null}
                        visualTitleSize={currentDraft.visual_title_size || 48}
                        visualTitlePosition={(currentDraft.media as any)?.visual_title_position || 'middle-center'}
                        showLogo={currentDraft.show_logo ?? true}
                        showSiteName={currentDraft.show_site_name ?? true}
                        showSiteUrl={currentDraft.show_site_url ?? true}
                        carouselUrls={((currentDraft.media as any)?.carousel?.items || []).map((i: any) => i.url)}
                      />
                    </div>
                    
                    {/* Story Preview */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Story (9:16)</p>
                      <BrandedPreview
                        format="story"
                        coverImageUrl={(currentDraft.media as any)?.story?.image_url || (currentDraft.media as any)?.cover?.url || null}
                        visualTitle={currentDraft.visual_title || null}
                        visualTitleSize={currentDraft.visual_title_size || 48}
                        visualTitlePosition={(currentDraft.media as any)?.visual_title_position || 'middle-center'}
                        showLogo={currentDraft.show_logo ?? true}
                        showSiteName={currentDraft.show_site_name ?? true}
                        showSiteUrl={currentDraft.show_site_url ?? true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Annuler
              </Button>
              <Button onClick={saveDraft}>
                {isCreating ? 'Créer le brouillon' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Éditeur d'image (filtres, crop, stickers) */}
        <Dialog open={editorOpen} onOpenChange={(o) => { setEditorOpen(o); if (!o) setEditingCarouselIdx(null); }}>
          <DialogContent className="p-0 gap-0 max-w-5xl w-screen h-[100dvh] sm:h-auto sm:max-h-[95vh] sm:w-auto sm:rounded-lg rounded-none flex flex-col overflow-hidden">
            {(() => {
              const carouselItems = ((currentDraft?.media as any)?.carousel?.items || []) as any[];
              const total = 1 + carouselItems.length;
              const currentIdx = editingCarouselIdx === null ? 0 : editingCarouselIdx + 1;
              const goTo = async (target: number) => {
                if (target < 0 || target >= total || target === currentIdx) return;
                await applyImageEdit();
                setEditingCarouselIdx(target === 0 ? null : target - 1);
                const items = ((currentDraft?.media as any)?.carousel?.items || []) as any[];
                const saved = target === 0
                  ? (currentDraft?.media as any)?.editor
                  : items[target - 1]?.editor;
                setEditorState(saved || DEFAULT_EDITOR_STATE);
                setEditorOpen(true);
              };
              return (
                <>
                  {/* Top bar — Instagram style */}
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-background sticky top-0 z-20 shrink-0">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditorOpen(false)} className="px-2">
                      Fermer
                    </Button>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Wand2 className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-sm font-medium truncate">
                        {editingCarouselIdx === null ? "Couverture" : `Diapo ${editingCarouselIdx + 2}`}
                      </span>
                      {total > 1 && <span className="text-[11px] text-muted-foreground">· {currentIdx + 1}/{total}</span>}
                    </div>
                    <Button type="button" size="sm" onClick={applyImageEdit} disabled={applyingEdit} className="px-3">
                      {applyingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : 'OK'}
                    </Button>
                  </div>

                  {/* Carousel pager dots/strip */}
                  {total > 1 && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b bg-background overflow-x-auto shrink-0">
                      {Array.from({ length: total }).map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => goTo(i)}
                          disabled={applyingEdit}
                          className={`shrink-0 h-7 px-2.5 rounded-full text-[11px] font-medium border transition ${
                            i === currentIdx ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'
                          }`}
                        >
                          {i === 0 ? 'Couv.' : `Diapo ${i + 1}`}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Body */}
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                    <DialogTitle className="sr-only">Éditeur d'image</DialogTitle>
                    <DialogDescription className="sr-only">Recadrez, filtres, stickers, textes — comme Instagram.</DialogDescription>
                    {getEditorSourceUrl() && (
                      <div className="p-2 sm:p-4">
                        <ImageEditor
                          imageUrl={getEditorSourceUrl() as string}
                          state={editorState}
                          onChange={setEditorState}
                        />
                      </div>
                    )}
                  </div>

                  {/* Bottom nav — only on mobile when carousel */}
                  {total > 1 && (
                    <div className="sm:hidden flex items-center justify-between gap-2 px-3 py-2 border-t bg-background shrink-0">
                      <Button type="button" variant="outline" size="sm" disabled={currentIdx === 0 || applyingEdit} onClick={() => goTo(currentIdx - 1)}>
                        ← Précédent
                      </Button>
                      <span className="text-xs text-muted-foreground">{currentIdx + 1} / {total}</span>
                      <Button type="button" variant="outline" size="sm" disabled={currentIdx >= total - 1 || applyingEdit} onClick={() => goTo(currentIdx + 1)}>
                        Suivant →
                      </Button>
                    </div>
                  )}
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Modal Preview complet */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Aperçu de la publication</DialogTitle>
              <DialogDescription>
                Vérifiez le rendu avant de publier
              </DialogDescription>
            </DialogHeader>
            
            {previewDraft && (
              <div className="grid md:grid-cols-2 gap-8">
                {/* Preview Feed (1080x1080) */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Format Feed (1:1)</Label>
                  <BrandedPreview
                    format="feed"
                    coverImageUrl={(previewDraft.media as any)?.cover?.url || null}
                    visualTitle={previewDraft.visual_title || null}
                    visualTitleSize={previewDraft.visual_title_size || 48}
                    visualTitlePosition={(previewDraft.media as any)?.visual_title_position || 'middle-center'}
                    showLogo={previewDraft.show_logo ?? true}
                    showSiteName={previewDraft.show_site_name ?? true}
                    showSiteUrl={previewDraft.show_site_url ?? true}
                    text={(previewDraft.copy as CopyContent)?.default || ''}
                    hashtags={(previewDraft.copy as CopyContent)?.instagram?.hashtags}
                    carouselUrls={((previewDraft.media as any)?.carousel?.items || []).map((i: any) => i.url)}
                  />
                </div>

                {/* Preview Story (1080x1920) */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Format Story (9:16)</Label>
                  <BrandedPreview
                    format="story"
                    coverImageUrl={(previewDraft.media as any)?.story?.image_url || (previewDraft.media as any)?.cover?.url || null}
                    visualTitle={previewDraft.visual_title || null}
                    visualTitleSize={previewDraft.visual_title_size || 48}
                    visualTitlePosition={(previewDraft.media as any)?.visual_title_position || 'middle-center'}
                    showLogo={previewDraft.show_logo ?? true}
                    showSiteName={previewDraft.show_site_name ?? true}
                    showSiteUrl={previewDraft.show_site_url ?? true}
                    text={(previewDraft.copy as CopyContent)?.story?.text || (previewDraft.copy as CopyContent)?.default || ''}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                Fermer
              </Button>
              {previewDraft && (previewDraft.status === 'published' || previewDraft.published_at) ? (
                <Button onClick={() => {
                  if (window.confirm("Republier ce post sur les réseaux sociaux sélectionnés ?")) {
                    setPreviewOpen(false);
                    handlePublish(previewDraft, { force: true });
                  }
                }}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Republier
                </Button>
              ) : previewDraft && (
                <Button onClick={() => {
                  setPreviewOpen(false);
                  handlePublish(previewDraft);
                }}>
                  <Send className="w-4 h-4 mr-2" />
                  Publier maintenant
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
}
